import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { trpc } from "../../../utils/trpc";
import { useConfig } from "../../hooks/useConfig";
import { LoadingSpinner } from "../../bits/loading";
import {
  extractChapterKey,
  getHeadingParent,
  getSectionFromContents,
} from "../../utils";
import type { ContentsItem } from "../WithPublication/PublicationContext";
import type { GhostPost } from "./ArticleContext";
import { ArticleContext } from "./ArticleContext";
import { ErrorPage } from "../../pages/ErrorPage";
import { NotFoundPage } from "../../pages/NotFoundPage";

// NOTE: This is very similar to PublicationProvider but using article-specific API routes and state.
// We may want to unify these in the future, e.g. shared hooks
export const ArticleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { frjDomain } = useConfig();
  const { articleSlug } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);

  // loadedArticleSlug tracks the slug whose data is fully committed to state.
  // All context values are held back until both queries settle for the current slug.
  const [loadedArticleSlug, setLoadedArticleSlug] = useState<
    string | undefined
  >(undefined);

  const [contents, setContents] = useState<ContentsItem[]>();
  const [chapters, setChapters] = useState<Record<string, string>>();
  const [styles, setStyles] = useState<string>();
  const [htmlChunks, setHtmlChunks] = useState<Record<number, string>>();
  const [title, setTitle] = useState<string | null>();
  const [ghostPost, setGhostPost] = useState<GhostPost | undefined>(undefined);
  const [currentSection, setCurrentSection] = useState<ContentsItem>();
  const [currentChunk, setCurrentChunk] = useState<string>();
  const [scrollDisabled, setScrollDisabled] = useState<boolean>();
  const [scrollSnippet, setScrollSnippet] = useState<string>();
  const [pendingHash, setPendingHash] = useState<string>();

  const {
    data: articleData,
    error: articleError,
    isFetching: articleFetching,
  } = trpc.getArticleForFRJ.useQuery(
    { slug: articleSlug as string },
    {
      enabled: !!articleSlug,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;
        if (!error.data) return true;
        if (String(error.data.httpStatus).startsWith("4")) return false;
        return true;
      },
    },
  );

  const selectByHash = useCallback((hash: string) => {
    if (window.location.hash.slice(1) === hash) return;
    setScrollDisabled(true);
    window.location.hash = `#${hash}`;
  }, []);

  const selectSection = useCallback(
    (id: string, section?: ContentsItem, noScroll = false) => {
      if (!contents) return;
      const contentsSection = section || getSectionFromContents(contents, id);
      if (contentsSection?.id && !contentsSection?.label) {
        const heading = getHeadingParent(contentsSection?.id, contents);
        contentsSection.label = heading?.label || "";
      }
      setCurrentSection(contentsSection);
      const chapterKey = extractChapterKey(id);
      const position = chapterKey ? chapters?.[chapterKey] : "0";
      if (position && position !== currentChunk) {
        setCurrentChunk(position);
      }

      if (noScroll) {
        history.replaceState(null, "", `#${id}`);
      } else {
        window.location.hash = id;
      }
    },
    [chapters, contents, currentChunk],
  );

  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash.slice(1);
      const chapterKey = extractChapterKey(hash);
      if (!chapterKey || !chapters) return;
      const positionStr = chapters[chapterKey];
      if (!positionStr) return;
      const position = +positionStr;
      setScrollDisabled(true);
      setCurrentChunk(`${position}`);
      setPendingHash(hash);
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [chapters]);

  useEffect(() => {
    if (articleFetching || !articleData) return;
    if (loadedArticleSlug === articleSlug) return;

    const parsedContents = articleData.contents as ContentsItem[];
    const newChapters = articleData.chapters;

    // FRJ articles may contain links to other FRJ articles. We want to rewrite these to point to our Article page instead
    // If the target is not indexed in compass the click handler will fall back to the FRJ URL
    const nextChunks: Record<number, string> = {};
    const frjOrigin = articleData.frjDomain
      ? new URL(articleData.frjDomain).origin
      : null;
    articleData.chunks.forEach((chunk, index) => {
      let html = chunk.content ?? "";
      if (frjOrigin) {
        const escaped = frjOrigin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        html = html.replace(
          new RegExp(`href="${escaped}/([^"]*)"`, "g"),
          (_, path) => {
            // drop any hash from the original link our hashes are based on chapter keys
            // and will never match
            const slug = path.split("#")[0].replace(/\/$/, "");
            return `href="/article/${slug}"`;
          },
        );
      }
      nextChunks[index] = html;
    });

    const hash = window.location.hash.slice(1);
    const chapterKey = extractChapterKey(hash);
    const position = chapterKey ? newChapters[chapterKey] : "0";
    if (!chapterKey) {
      window.location.replace(window.location.href + `#${articleSlug}-ch:0`);
    }
    const section = getSectionFromContents(
      parsedContents,
      hash || `${articleSlug}-ch:0`,
    );

    setContents(parsedContents);
    setChapters(newChapters);
    setStyles(articleData.styleSheet);
    setTitle(parsedContents[0]?.label ?? parsedContents[0]?.name ?? null);
    setGhostPost(articleData.ghostPost ?? undefined);
    setHtmlChunks(nextChunks);
    setCurrentChunk(position ?? "0");
    setCurrentSection(section);
    setLoadedArticleSlug(articleSlug);
  }, [articleData, articleFetching, articleSlug, loadedArticleSlug]);

  const context = useMemo(
    () => ({
      contents,
      setContents,
      chapters,
      styles,
      htmlChunks,
      articleSlug:
        loadedArticleSlug === articleSlug ? articleSlug : loadedArticleSlug,
      title,
      currentSection,
      selectSection,
      currentChunk,
      selectByHash,
      scrollDisabled,
      setScrollDisabled,
      scrollSnippet,
      setScrollSnippet,
      pendingHash,
      setPendingHash,
      containerRef,
      ghostPost,
    }),
    [
      contents,
      chapters,
      styles,
      htmlChunks,
      loadedArticleSlug,
      articleSlug,
      title,
      currentSection,
      selectSection,
      currentChunk,
      selectByHash,
      scrollDisabled,
      scrollSnippet,
      pendingHash,
      ghostPost,
    ],
  );

  // if we get a 404 from the article query, it's likely the page is available on FRJ but not indexed in Compass.
  // In this case we want to redirect to the FRJ URL rather than show an error page.
  useEffect(() => {
    if (articleError?.message !== "Article not found") return;
    if (!frjDomain || !articleSlug) return;
    window.location.replace(`${frjDomain}/${articleSlug}`);
  }, [articleError, articleSlug, frjDomain]);

  if (articleError?.message === "Article not found") {
    if (frjDomain) return <LoadingSpinner />;
    return <NotFoundPage message="This article could not be found." />;
  }

  if (articleError) return <ErrorPage message={articleError.message} />;

  if (!contents) return <LoadingSpinner />;

  return (
    <ArticleContext.Provider value={context}>
      {children}
    </ArticleContext.Provider>
  );
};
